import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { authFetch, API } from './api';
import { Building2, Plus, Edit2, Trash2, X, Check, AlertTriangle, Users, ClipboardList } from 'lucide-react';

export default function SchoolManagement() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', state: '', city: '' });
  const [editForm, setEditForm] = useState({ name: '', state: '', city: '', subscription_status: '' });

  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
  ];

  useEffect(() => { loadSchools(); }, []);

  const loadSchools = async () => {
    try {
      const res = await authFetch(`${API}/api/schools/`);
      if (res.ok) setSchools(await res.json());
    } catch (err) { console.error('Failed to load schools:', err); }
    finally { setLoading(false); }
  };

  const handleAddSchool = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await authFetch(`${API}/api/schools/`, {
        method: 'POST',
        body: JSON.stringify(form)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to create school'); }
      setSuccess(`School "${form.name}" created successfully`);
      setForm({ name: '', state: '', city: '' });
      setShowAddForm(false);
      loadSchools();
    } catch (err) { setError(err.message); }
  };

  const handleEditSchool = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await authFetch(`${API}/api/schools/${editingSchool.id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to update school'); }
      setSuccess(`Updated "${editForm.name}"`);
      setEditingSchool(null);
      loadSchools();
    } catch (err) { setError(err.message); }
  };

  const handleDeleteSchool = async (school) => {
    setError(''); setSuccess('');
    try {
      const res = await authFetch(`${API}/api/schools/${school.id}`, { method: 'DELETE' });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to delete school'); }
      setSuccess(`Deleted "${school.name}" and all associated data`);
      setDeleteConfirm(null);
      loadSchools();
    } catch (err) { setError(err.message); }
  };

  const startEdit = (s) => {
    setEditingSchool(s);
    setEditForm({ name: s.name, state: s.state || '', city: s.city || '', subscription_status: s.subscription_status || 'active' });
    setShowAddForm(false);
    setDeleteConfirm(null);
    setError('');
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-600/20 text-green-300 border-green-600/30',
      inactive: 'bg-red-600/20 text-red-300 border-red-600/30',
      trial: 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30',
    };
    return (
      <span className={`px-2 py-0.5 rounded border text-xs font-medium ${styles[status] || 'bg-gray-600/20 text-gray-300 border-gray-600/30'}`}>
        {status || 'active'}
      </span>
    );
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-[#0A0E27] pt-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-[#1A2744] rounded-xl p-8 text-center border border-[#546E7A]/30">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-white text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-gray-400">Only super administrators can manage schools.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#00D4FF]" />
              School Management
            </h1>
            <p className="text-gray-400 text-sm mt-1">Manage all schools and organizations</p>
          </div>
          <button
            onClick={() => { setShowAddForm(!showAddForm); setEditingSchool(null); setDeleteConfirm(null); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add School
          </button>
        </div>

        {/* Success / Error */}
        {success && (
          <div className="bg-green-900/30 border border-green-700/50 text-green-300 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
            <Check className="w-4 h-4" /> {success}
          </div>
        )}
        {error && !showAddForm && !editingSchool && (
          <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        {/* Add School Form */}
        {showAddForm && (
          <div className="bg-[#1A2744] rounded-xl p-6 border border-[#546E7A]/30 mb-6">
            <h3 className="text-white font-semibold mb-4">Add New School</h3>
            {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            <form onSubmit={handleAddSchool} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">School Name</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Lincoln High School" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
                  <input type="text" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Atlanta" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">State</label>
                  <select value={form.state} onChange={(e) => setForm({...form, state: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                    <option value="">Select state</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" className="px-6 py-2.5 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90">Create School</button>
                <button type="button" onClick={() => { setShowAddForm(false); setError(''); }} className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Edit School Form */}
        {editingSchool && (
          <div className="bg-[#1A2744] rounded-xl p-6 border border-[#00D4FF]/30 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Edit: {editingSchool.name}</h3>
              <button onClick={() => setEditingSchool(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            <form onSubmit={handleEditSchool} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">School Name</label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
                  <input type="text" value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">State</label>
                  <select value={editForm.state} onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                    <option value="">Select state</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-300 mb-1">Subscription Status</label>
                <select value={editForm.subscription_status} onChange={(e) => setEditForm({...editForm, subscription_status: e.target.value})}
                  className="w-full px-4 py-2.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button type="submit" className="px-6 py-2.5 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90">Save Changes</button>
                <button type="button" onClick={() => setEditingSchool(null)} className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="bg-red-900/20 rounded-xl p-6 border border-red-600/30 mb-6">
            <h3 className="text-red-300 font-semibold mb-2">Delete "{deleteConfirm.name}"?</h3>
            <p className="text-gray-400 text-sm mb-4">
              This will permanently delete the school and ALL associated data including {deleteConfirm.player_count} player(s), {deleteConfirm.user_count} user(s), all grades, fit scores, and reports. This cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => handleDeleteSchool(deleteConfirm)}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm hover:bg-red-700">Yes, Delete Everything</button>
              <button onClick={() => setDeleteConfirm(null)}
                className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
            </div>
          </div>
        )}

        {/* Schools List */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#546E7A]/30">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#00D4FF]" />
              All Schools
              <span className="text-gray-400 font-normal text-sm ml-2">({schools.length})</span>
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading schools...</div>
          ) : schools.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No schools found</div>
          ) : (
            <div className="divide-y divide-[#546E7A]/20">
              {schools.map((s) => (
                <div key={s.id} className="px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 hover:bg-[#243B55]/30 transition-colors">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#243B55] flex items-center justify-center text-[#00D4FF] font-bold text-sm">
                      {s.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">{s.name}</div>
                      <div className="text-gray-400 text-xs">{s.city}{s.city && s.state ? ', ' : ''}{s.state}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {s.player_count} players</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {s.user_count} users</span>
                    </div>
                    {getStatusBadge(s.subscription_status)}
                    <div className="flex items-center gap-1 ml-2">
                      <button onClick={() => startEdit(s)} title="Edit school"
                        className="p-1.5 text-gray-400 hover:text-[#00D4FF] hover:bg-[#243B55] rounded transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setDeleteConfirm(s); setEditingSchool(null); setShowAddForm(false); }} title="Delete school"
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-[#243B55] rounded transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
